import { useCallback, useEffect, useState } from 'react';
import {
    ALL_CERTIFICATION_STATUSES_FILTER,
    filterMembersByCertificationStatus
} from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection/constants';
import useTranslation from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection/hooks/useTranslation';
import type { OrganizationMember } from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection/types';

import useToast from 'hooks/useToast';
import useUsers from 'hooks/useUsers';

const useUsersTable = () => {
    const { fetchTeamMembers } = useUsers();
    const { broadcastToast } = useToast();
    const { LOAD_ERROR } = useTranslation();

    const [members, setMembers] = useState<OrganizationMember[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const [certificationStatusFilter, setCertificationStatusFilter] = useState(
        ALL_CERTIFICATION_STATUSES_FILTER
    );

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const { data, error } = await fetchTeamMembers(pageToken);

            if (error || !data) {
                throw new Error(LOAD_ERROR);
            }

            setMembers(currentMembers =>
                pageToken ? [...currentMembers, ...data.objects] : data.objects
            );
            setHasNextPage(data.hasNext);
            setNextPageToken(data.nextPageToken ?? null);
        },
        [LOAD_ERROR, fetchTeamMembers]
    );

    useEffect(() => {
        const loadFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            }

            setIsLoading(false);
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            broadcastToast({ text: LOAD_ERROR, type: 'error' });
        } finally {
            setIsFetchingMore(false);
        }
    }, [LOAD_ERROR, broadcastToast, hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleCertificationStatusFilterChange = useCallback(
        (nextFilter: string) => setCertificationStatusFilter(nextFilter),
        []
    );

    return {
        certificationStatusFilter,
        handleCertificationStatusFilterChange,
        handleLoadMore,
        hasMembers: members.length > 0,
        hasNextPage,
        isFetchingMore,
        isLoading,
        members: filterMembersByCertificationStatus(members, certificationStatusFilter)
    };
};

export default useUsersTable;
